<template>
  <div
    class="mbti-report-container"
    v-loading="loading"
    element-loading-text="正在生成专属报告..."
  >
    <!-- 顶部导航栏 -->
    <div class="nav-bar">
      <i
        class="el-icon-arrow-left back-icon"
        @click="$router.push('/mbti-home')"
      ></i>
      <span class="title">MBTI性格测试</span>
      <div class="capsule"></div>
    </div>

    <!-- 主体内容区 (数据加载完成后显示) -->
    <div class="main-content" v-if="!loading">
      <!-- 头部卡片 -->
      <div class="header-card">
        <h2 class="report-title">你的专属MBTI测评报告已生成</h2>
        <p class="report-time">生成于：{{ reportData.createTime }}</p>

        <div class="user-info-box">
          <img
            :src="
              require('@/assets/images/mbti-icon/' +
                (reportData.mbtiCode || 'ENTJ') +
                '.png')
            "
            :alt="reportData.mbtiCode || 'ENTJ'"
            class="avatar"
          />
          <div class="type-info">
            <span class="label">你的测试类型是</span>
            <span class="mbti-code">{{ reportData.mbtiCode }}</span>
            <span class="mbti-name">{{ reportData.mbtiName }}</span>
          </div>
        </div>

        <!-- 动态标签 -->
        <div class="tags-row">
          <span
            class="tag"
            v-for="(tag, index) in reportData.tags"
            :key="index"
            :class="getTagColorClass(index)"
          >
            #{{ tag }}
          </span>
        </div>

        <!-- 标语 (接口未提供，使用默认值) -->
        <div class="slogan-btn">“{{ reportData.slogan }}”</div>

        <!-- 动态类型数据条 -->
        <div class="data-section">
          <div class="data-title">类型数据</div>
          <div
            class="progress-item"
            v-for="(item, index) in reportData.progressData"
            :key="index"
          >
            <div class="label-left">
              {{ item.leftLabel }} {{ Math.round(item.leftPercent) }}%
            </div>
            <div class="progress-track">
              <!-- 动态计算宽度和颜色 -->
              <div
                class="progress-fill"
                :style="{
                  width: Math.round(item.leftPercent) + '%',
                  backgroundColor: getProgressColor(index),
                }"
              ></div>
            </div>
            <div class="label-right">
              {{ Math.round(item.rightPercent) }}% {{ item.rightLabel }}
            </div>
          </div>
        </div>
      </div>

      <!-- 详细报告卡片 -->
      <div class="detail-card">
        <div class="paper-clip">📎</div>

        <div
          class="section"
          v-for="(section, index) in reportData.reportDetails"
          :key="index"
        >
          <div class="section-title">
            <span class="line"></span>
            <span class="text">{{ section.title }}</span>
            <span class="line"></span>
          </div>
          <div class="section-content">
            <!-- 按换行符分割长文本 -->
            <p
              v-for="(p, pIndex) in formatParagraphs(section.content)"
              :key="pIndex"
              :class="{ 'bullet-point': p.startsWith('*') }"
            >
              {{ p }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部固定按钮区 -->
    <div class="footer-bar">
      <el-button class="btn-left" type="info" @click="shareLink"
        >分享链接</el-button
      >
      <el-button class="btn-right" type="warning" @click="restartTest"
        >再测一次</el-button
      >
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { copyText } from "@/utils/clipboard";

export default {
  name: "MbtiReport",
  data() {
    return {
      loading: true,
      reportData: {
        createTime: "",
        mbtiCode: "",
        mbtiName: "",
        tags: [],
        slogan: "认识自己，成就未来", // 接口没返回，给个通用的
        progressData: [],
        reportDetails: [],
      },
      tagColors: ["tag-orange", "tag-green", "tag-blue", "tag-purple"],
      progressColors: ["#8b5cf6", "#3b82f6", "#f97316", "#10b981"],
    };
  },
  created() {
    this.initData();
  },
  methods: {
    initData() {
      let openid = "";
      let wjId =
        this.$route.query.wjId || localStorage.getItem("mbtiTestWjId") || "1";

      if (this.$route && this.$route.query && this.$route.query.openid) {
        openid = this.$route.query.openid;
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        openid =
          urlParams.get("openid") ||
          localStorage.getItem("openId") ||
          localStorage.getItem("openid") ||
          "";
        wjId = urlParams.get("wjId") || wjId;
      }

      if (!openid || !wjId) {
        this.$message.error("缺少必要参数，无法获取报告");
        this.loading = false;
        return;
      }

      this.fetchReportData(openid, wjId);
    },

    async fetchReportData(openid, wjId) {
      this.loading = true;
      try {
        const apiUrl = `http://59.110.51.52:8010/system/web/getsurvey?openid=${encodeURIComponent(
          openid
        )}&wjId=${wjId}`;

        const response = await axios.get(apiUrl);
        const resData = response.data;

        if (resData.code === 200) {
          const apiData = resData.data;
          const scores = apiData.score;

          // 核心数据映射
          this.reportData = {
            createTime: this.getCurrentTime(), // 接口没给时间，自动生成当前时间
            mbtiCode: apiData.type, // 如 "ESTJ"
            mbtiName: apiData.typeName, // 如 "管家型"
            // tags: this.generateTags(apiData.type), // 根据字母生成中文标签
            tags: apiData.feature, // 根据字母生成中文标签
            slogan: apiData.blurb, // 你可以根据需要修改

            // 解析百分比数据 (parseFloat 去掉 '%' 符号以便于计算进度条宽度)
            progressData: [
              {
                leftLabel: "外向 (E)",
                leftPercent: parseFloat(scores.E),
                rightLabel: "内向 (I)",
                rightPercent: parseFloat(scores.I),
              },
              {
                leftLabel: "实感 (S)",
                leftPercent: parseFloat(scores.S),
                rightLabel: "直觉 (N)",
                rightPercent: parseFloat(scores.N),
              },
              {
                leftLabel: "理性 (T)",
                leftPercent: parseFloat(scores.T),
                rightLabel: "感性 (F)",
                rightPercent: parseFloat(scores.F),
              },
              {
                leftLabel: "判断 (J)",
                leftPercent: parseFloat(scores.J),
                rightLabel: "知觉 (P)",
                rightPercent: parseFloat(scores.P),
              },
            ],

            // 映射详情数组
            reportDetails: apiData.detail.map((item) => ({
              title: item.dimension,
              content: item.detail,
            })),
          };
        } else {
          this.$message.error(resData.msg || "获取数据失败");
        }
      } catch (error) {
        console.error("接口请求失败:", error);
        this.$message.error("网络请求失败，请稍后再试");
      } finally {
        this.loading = false;
      }
    },

    // --- 辅助方法区 ---

    // 1. 处理长文本：按换行符切割
    formatParagraphs(text) {
      if (!text) return [];
      return text.split(/\r?\n/).filter((p) => p.trim() !== "");
    },

    // 2. 根据四个字母生成中文标签
    generateTags(typeCode) {
      if (!typeCode) return [];
      const tagMap = {
        E: "外倾",
        I: "内倾",
        S: "实感",
        N: "直觉",
        T: "思维",
        F: "情感",
        J: "判断",
        P: "知觉",
      };
      // 将 "ESTJ" 拆分为 ['E', 'S', 'T', 'J'] 然后映射出中文
      return typeCode.split("").map((char) => tagMap[char] || char);
    },

    // 3. 获取当前时间 (YYYY-MM-DD HH:mm:ss)
    getCurrentTime() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      const h = String(now.getHours()).padStart(2, "0");
      const min = String(now.getMinutes()).padStart(2, "0");
      const s = String(now.getSeconds()).padStart(2, "0");
      return `${y}-${m}-${d} ${h}:${min}:${s}`;
    },

    // 4. 获取颜色Class
    getTagColorClass(index) {
      return this.tagColors[index % this.tagColors.length];
    },

    // 辅助方法：动态获取进度条颜色
    getProgressColor(index) {
      return this.progressColors[index % this.progressColors.length];
    },

    // 分享链接方法
    shareLink() {
      // 构建分享链接
      const shareUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

      copyText(shareUrl)
        .then(() => {
          this.$message.success("链接已复制，去分享给好友吧！");
        })
        .catch((err) => {
          console.error("复制失败:", err);
          this.$message.error("复制失败，请手动复制链接");
        });
    },

    // 再测一次方法
    restartTest() {
      this.$router.push("/mbti-qa");
    },
  },
};
</script>

<style scoped>
/* 样式部分保持不变，确保完美还原设计图 */
.mbti-report-container {
  max-width: 480px;
  margin: 0 auto;
  background-color: #fff6eb;
  min-height: 100vh;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", Arial, sans-serif;
  position: relative;
  padding-bottom: 70px;
}

.main-content {
  padding: 15px;
}

.header-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px 15px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
}
.report-title {
  font-size: 18px;
  color: #333;
  margin: 0 0 5px 0;
}
.report-time {
  font-size: 11px;
  color: #999;
  margin: 0 0 20px 0;
}

.user-info-box {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff9f2;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
}
.avatar {
  width: 80px;
  height: 80px;
  margin-right: 20px;
}
.type-info {
  text-align: left;
  display: flex;
  flex-direction: column;
}
.type-info .label {
  font-size: 12px;
  color: #999;
}
.type-info .mbti-code {
  font-size: 32px;
  font-weight: 900;
  color: #333;
  line-height: 1.2;
}
.type-info .mbti-name {
  font-size: 14px;
  color: #e67e22;
  font-weight: bold;
}

.tags-row {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 15px;
}
.tag {
  font-size: 14px;
  font-weight: bold;
}
.tag-orange {
  color: #f39c12;
}
.tag-green {
  color: #27ae60;
}
.tag-blue {
  color: #2980b9;
}
.tag-purple {
  color: #8e44ad;
}

.slogan-btn {
  background: linear-gradient(90deg, #f39c12, #e67e22);
  color: #fff;
  border-radius: 20px;
  padding: 8px 10px;
  font-size: 14px;
  font-weight: bold;
  width: max-content;
  margin: 0 auto 25px auto;
  box-shadow: 0 4px 10px rgba(230, 126, 34, 0.3);
}

.data-section {
  text-align: left;
}
.data-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
}
.progress-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 12px;
  color: #666;
}
.label-left {
  width: 85px;
  text-align: right;
}
.label-right {
  width: 85px;
  text-align: left;
}
.progress-track {
  flex: 1;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  margin: 0 10px;
  position: relative;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.8s ease-in-out;
}

.detail-card {
  background: #fff;
  border-radius: 16px;
  padding: 30px 20px 20px 20px;
  margin-top: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  position: relative;
}

.paper-clip {
  position: absolute;
  top: -15px;
  right: 20px;
  font-size: 30px;
  transform: rotate(15deg);
  color: #999;
  z-index: 2;
}

.section {
  margin-bottom: 30px;
}
.section-title {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}
.section-title .text {
  font-size: 18px;
  font-weight: bold;
  color: #e67e22;
  margin: 0 15px;
}
.section-title .line {
  width: 30px;
  height: 1px;
  background-color: #f8c499;
}

.section-content {
  font-size: 14px;
  color: #444;
  line-height: 1.8;
  text-align: justify;
}
.section-content p {
  margin: 0 0 10px 0;
}
.section-content .bullet-point {
  padding-left: 10px;
  position: relative;
}

.footer-bar {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  display: flex;
  background: #fff;
  padding: 10px 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 100;
}
.footer-bar .el-button {
  flex: 1;
  border: none;
  border-radius: 0;
  height: 50px;
  font-size: 16px;
  font-weight: bold;
  margin: 0;
}
.btn-left {
  background-color: #999 !important;
  color: #fff !important;
}
.btn-right {
  background-color: #e67e22 !important;
  color: #fff !important;
}
</style>
